import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';

const JournalPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const posts = [
    {
      id: 'maldives-luxury-guide',
      title: 'The Ultimate Maldives Luxury Guide',
      excerpt: 'Discover the most exclusive resorts, private islands, and underwater dining experiences in the Maldives.',
      image: 'https://images.unsplash.com/photo-1620483829646-8fb1d8162108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxtYWxkaXZlcyUyMGx1eHVyeSUyMHdhdGVyJTIwdmlsbGElMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3NzMxNDQ1NTR8MA&ixlib=rb-4.1.0&q=85',
      category: 'Destination Guides',
      date: 'January 15, 2026',
    },
    {
      id: 'rajasthan-palaces',
      title: 'Royal Rajasthan: A Palace Hotel Journey',
      excerpt: 'Experience India\'s maharaja heritage through stays in converted palace hotels across Rajasthan.',
      image: 'https://images.unsplash.com/photo-1770665567877-72ee8a7c9051?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx1ZGFpcHVyJTIwbGFrZSUyMHBhbGFjZSUyMGx1eHVyeSUyMGhvdGVsJTIwaW5kaWF8ZW58MHx8fHwxNzczMTQ0NTU0fDA&ixlib=rb-4.1.0&q=85',
      category: 'Travel Inspiration',
      date: 'January 10, 2026',
    },
    {
      id: 'safari-season',
      title: 'Best Season for African Safari',
      excerpt: 'When to witness the Great Migration and how to choose the perfect luxury safari camp.',
      image: 'https://images.unsplash.com/photo-1709402606682-400133d92ab2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB0cmF2ZWwlMjBzYWZhcmklMjBzZXJlbmdldGklMjBzdW5zZXQlMjBwcml2YXRlJTIwamVlcHxlbnwwfHx8fDE3NzMxNDQ1NTN8MA&ixlib=rb-4.1.0&q=85',
      category: 'Travel Trends',
      date: 'January 5, 2026',
    },
    {
      id: 'luxury-trains',
      title: 'World\'s Most Luxurious Train Journeys',
      excerpt: 'From the Orient Express to Rocky Mountaineer, explore train travel at its most opulent.',
      image: 'https://images.unsplash.com/photo-1474487548417-781cb71495f3?q=80&w=2574&auto=format&fit=crop',
      category: 'Luxury Hotel Spotlights',
      date: 'December 28, 2025',
    },
    {
      id: 'japanese-ryokan',
      title: 'The Art of the Japanese Ryokan',
      excerpt: 'Understanding traditional Japanese hospitality and luxury in historic ryokan inns.',
      image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2670&auto=format&fit=crop',
      category: 'Destination Guides',
      date: 'December 20, 2025',
    },
    {
      id: 'amalfi-villas',
      title: 'Private Villas of the Amalfi Coast',
      excerpt: 'Cliffside luxury and Mediterranean elegance in Italy\'s most stunning coastal region.',
      image: 'https://images.unsplash.com/photo-1534445538923-ab3daf341b3c?q=80&w=2574&auto=format&fit=crop',
      category: 'Luxury Hotel Spotlights',
      date: 'December 15, 2025',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="journal-page"
    >
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden" data-testid="journal-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            Travel Inspiration
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary"
            data-testid="journal-title"
          >
            The Journal
          </motion.h1>
        </div>
      </section>

      {/* Featured Post */}
      <section className="section-padding container-padding" data-testid="journal-featured">
        <div className="max-w-[1920px] mx-auto">
          <Link to={`/journal/${posts[0].id}`} className="block group">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={posts[0].image}
                  alt={posts[0].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              <div>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-accent mb-4">
                  Featured · {posts[0].category}
                </p>
                <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6 group-hover:text-accent transition-colors duration-300">
                  {posts[0].title}
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  {posts[0].excerpt}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-accent" />
                    <span>{posts[0].date}</span>
                  </div>
                  <span className="btn-secondary inline-flex items-center gap-2">
                    Read More <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="journal-grid">
        <div className="max-w-[1920px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {posts.slice(1).map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                data-testid={`journal-post-${post.id}`}
              >
                <Link to={`/journal/${post.id}`} className="block group">
                  <div className="aspect-[4/3] overflow-hidden mb-6">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  <p className="font-body text-xs uppercase tracking-[0.2em] text-accent mb-3">
                    {post.category}
                  </p>

                  <h3 className="font-heading text-2xl md:text-3xl font-light text-primary mb-4 group-hover:text-accent transition-colors duration-300">
                    {post.title}
                  </h3>

                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar size={12} className="text-accent" />
                    <span>{post.date}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default JournalPage;